import { useState, useEffect } from "react";
import axios from "axios";
import { WalletHeader } from "../../Components/Wallet/WalletHeader";
import { BalanceCard } from "../../Components/Wallet/BalanceCard";
import { TransactionList } from "../../Components/Wallet/TransactionList";
import { AddMoneyModal } from "../../Components/Wallet/AddMoneyModal";
import { loadRazorpay } from "../../Components/Wallet/Razorpay";
import { toast } from "react-toastify";
import Select from "react-select";
import { AgentBalanceCard } from "../../Components/Wallet/AgentBalanceCard";
import Modal from "../../Components/PortalModal";
import logodefault from "../../Assets/Logos/logov.png";
import { generateReceiptPDF } from "../../utils/receiptGenerator";

const Wallet = () => {
  const [activeTab, setActiveTab] = useState("transactions");
  const [activeWallet, setActiveWallet] = useState("my"); // Default to 'my' wallet
  const [userRole, setUserRole] = useState("");
  const [showAddMoneyModal, setShowAddMoneyModal] = useState(false);
  const [addMoneyAmount, setAddMoneyAmount] = useState("");
  const [razorpayKey, setRazorpayKey] = useState("");
  const [user, setUser] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(0);
  const [currency, setCurrency] = useState("INR");

  // For agent/subagent selection
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [subagents, setSubagents] = useState([]);
  const [selectedSubagent, setSelectedSubagent] = useState(null);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [loadingSubagents, setLoadingSubagents] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [transactionsPerPage] = useState(10);
  const [totalTransactions, setTotalTransactions] = useState(0);

  // Transfer modal states
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState("");
  const [transferTarget, setTransferTarget] = useState(null);
  const [transferNote, setTransferNote] = useState("");
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferOptions, setTransferOptions] = useState([]);

  // Deduct modal states
  const [showDeductModal, setShowDeductModal] = useState(false);
  const [deductAmount, setDeductAmount] = useState("");
  const [deductTarget, setDeductTarget] = useState(null);
  const [deductNote, setDeductNote] = useState("");
  const [deductLoading, setDeductLoading] = useState(false);
  const [deductOptions, setDeductOptions] = useState([]);
  const [deductedFor, setDeductedFor] = useState("");

  // Admin direct credit modal states
  const [showAdminCreditModal, setShowAdminCreditModal] = useState(false);
  const [adminCreditAmount, setAdminCreditAmount] = useState("");
  const [adminCreditNote, setAdminCreditNote] = useState("");
  const [adminCreditTarget, setAdminCreditTarget] = useState(null);
  const [adminCreditLoading, setAdminCreditLoading] = useState(false);
  const [adminCreditOptions, setAdminCreditOptions] = useState([]);

  // PDF Receipt States
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastTransactionDetails, setLastTransactionDetails] = useState(null);
  const [logo, setLogo] = useState("");

  const checkSpecialagent = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/special-agent/check-special-agent-child`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      const responseData = response.data.agentData;
      if (responseData && responseData.logo) {
        setLogo(responseData.logo);
      } else {
        setLogo(logodefault);
      }
    } catch (e) {
      console.error("Failed to fetch special agent logo:", e);
      setLogo(logodefault);
    }
  };

  useEffect(() => {
    checkSpecialagent();
  }, []);

  const generateReceiptId = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    const year = new Date().getFullYear();
    return `${year}-${result}`;
  };

  const generateInvoiceNumber = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let result = "";
    for (let i = 8; i > 0; --i) result += chars[Math.floor(Math.random() * chars.length)];
    return `${result}-0001`;
  };

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const parsed = JSON.parse(userData);
      setUser(parsed);
      const role = parsed.role?.toLowerCase() || "admin";
      setUserRole(role);
      // Set default wallet view based on role
      setActiveWallet("my");
    }
  }, []);

  useEffect(() => {
    loadRazorpay();
    fetchRazorpayKey();
  }, []);

  useEffect(() => {
    if (userRole && user) {
      if (userRole === "admin" && activeWallet === "agents") {
        fetchAgents();
      } else if (
        (userRole === "admin" || userRole === "agent") &&
        activeWallet === "subagents"
      ) {
        fetchSubagents();
      } else if (activeWallet === "my") {
        fetchWalletData(user.entityId);
      }
    }
  }, [activeWallet, userRole, user, selectedAgent]);

  // Fetch transfer options based on role and wallet tab
  useEffect(() => {
    if (!user) return;
    if (userRole === "admin" && activeWallet !== "my") {
      axios
        .get(`${process.env.REACT_APP_API_URL}/users/get-all-agents`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        })
        .then((res) => {
          setTransferOptions(
            res.data
              .filter((u) => u.entityId !== user.entityId)
              .map((u) => ({
                value: u.entityId,
                label: `${u.name} (${u.entityId})`,
                name: u.name,
                phone: u.phone,
                entityId: u.entityId,
                email: u.email || "",
              })),
          );
        });
    } else if (userRole === "agent" && activeWallet === "subagents") {
      // Agent can transfer to their subagents
      axios
        .get(`${process.env.REACT_APP_API_URL}/users/get-all-sub-agents`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          params: { parentId: user._id },
        })
        .then((res) => {
          setTransferOptions(
            res.data.map((u) => ({
              value: u.entityId,
              label: `${u.name} (${u.entityId})`,
              name: u.name,
              phone: u.phone,
              entityId: u.entityId,
              email: u.email || "",
            })),
          );
        });
    } else {
      setTransferOptions([]);
    }
  }, [user, userRole, activeWallet]);

  const fetchRazorpayKey = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/key`,
      );
      if (response.data.success) {
        setRazorpayKey(response.data.key);
      }
    } catch (error) {
      console.error("Error fetching Razorpay key:", error);
    }
  };

  const fetchAgents = async () => {
    try {
      setLoadingAgents(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/get-all-agents`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );
      setAgents(
        response.data.map((agent) => ({
          value: agent.entityId,
          label: `${agent.name} (${agent.entityId})`,
          name: agent.name,
          phone: agent.phone,
          entityId: agent.entityId,
          email: agent.email || "",
          ...agent,
        })),
      );
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast.error("Failed to fetch agents");
    } finally {
      setLoadingAgents(false);
    }
  };

  const fetchSubagents = async () => {
    try {
      setLoadingSubagents(true);
      let parentId = selectedAgent?._id;

      // If user is agent, use their own ID as parentId
      if (userRole === "agent" && activeWallet === "subagents") {
        parentId = user?._id;
      }

      if (!parentId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/get-all-sub-agents`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
          params: { parentId },
        },
      );
      setSubagents(
        response.data.map((subagent) => ({
          value: subagent.entityId,
          label: `${subagent.name} (${subagent.entityId})`,
          name: subagent.name,
          phone: subagent.phone,
          entityId: subagent.entityId,
          email: subagent.email || "",
          ...subagent,
        })),
      );
    } catch (error) {
      console.error("Error fetching subagents:", error);
      toast.error("Failed to fetch subagents");
    } finally {
      setLoadingSubagents(false);
    }
  };

  const fetchWalletData = async (entityId) => {
    if (!entityId) return;

    try {
      setLoading(true);
      // Clear previous data while loading
      setBalance(0);
      setTransactions([]);

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/wallet/${entityId}`,
      );

      if (response.data.success) {
        // Make sure we're getting the correct wallet data
        setBalance(response.data.balance);
        setCurrency(response.data.currency);

        const sortedTransactions =
          response.data.walletDetails?.transactions?.sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
          ) || [];

        setTransactions(sortedTransactions);
        setTotalTransactions(sortedTransactions.length);
      }
    } catch (error) {
      console.error("Error fetching wallet data:", error);
      toast.error("Failed to fetch wallet data");
      setBalance(0);
      setTransactions([]);
      setTotalTransactions(0);
    } finally {
      setLoading(false);
    }
  };

  const handleAgentChange = (selectedOption) => {
    setSelectedAgent(selectedOption);
    setSelectedSubagent(null);
    // Reset balance and transactions while loading new data
    setBalance(0);
    setTransactions([]);
    if (activeWallet === "agents") {
      // View selected agent's wallet
      fetchWalletData(selectedOption.value);
    }
  };

  const handleSubagentChange = (selectedOption) => {
    setSelectedSubagent(selectedOption);
    // Reset balance and transactions while loading new data
    setBalance(0);
    setTransactions([]);
    // View selected subagent's wallet
    fetchWalletData(selectedOption.value);
  };

  const handleWalletTabChange = (tab) => {
    setActiveWallet(tab);
    setSelectedAgent(null);
    setSelectedSubagent(null);
    // Reset balance when switching tabs
    setBalance(0);
    setTransactions([]);

    // Fetch appropriate data when tab changes
    if (tab === "my" && user) {
      fetchWalletData(user.entityId);
    }
  };
  const indexOfLastTransaction = currentPage * transactionsPerPage;
  const indexOfFirstTransaction = indexOfLastTransaction - transactionsPerPage;
  const currentTransactions = transactions.slice(
    indexOfFirstTransaction,
    indexOfLastTransaction,
  );
  const totalPages = Math.ceil(totalTransactions / transactionsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));
  };

  const renderWalletTitle = () => {
    switch (activeWallet) {
      case "my":
        return "My Wallet";
      case "agents":
        return selectedAgent
          ? `${selectedAgent.label}'s Wallet`
          : "Select an Agent";
      case "subagents":
        return selectedSubagent
          ? `${selectedSubagent.label}'s Wallet`
          : "Select a Subagent";
      default:
        return "Wallet";
    }
  };

  const validateAgentLimit = (amount) => {
    if (userRole === "agent" || userRole === "subagent") {
      return amount <= 5000;
    }
    return true;
  };

  // Admin: Add money to own wallet (master wallet load)
  const handleAddMoney = async () => {
    if (
      !addMoneyAmount ||
      isNaN(addMoneyAmount) ||
      Number(addMoneyAmount) <= 0
    ) {
      toast.error("Enter a valid amount");
      return;
    }
    if (userRole === "admin" && activeWallet === "my") {
      try {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/wallet/admin-credit`,
          {
            toEntityId: user.entityId,
            amount: Number(addMoneyAmount),
            note: "Admin wallet load",
          },
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          },
        );
        setShowAddMoneyModal(false);
        setAddMoneyAmount("");
        await fetchWalletData(user.entityId);
        toast.success("Money added to your wallet");
      } catch (err) {
        toast.error(err?.response?.data?.message || "Failed to add money");
      }
    }
  };

  // Admin: Transfer to agent/subagent
  const handleTransfer = async () => {
    let toEntityId = null;
    let target = null;
    if (activeWallet === "agents" && selectedAgent) {
      toEntityId = selectedAgent.value;
      target = selectedAgent;
    } else if (activeWallet === "subagents" && selectedSubagent) {
      toEntityId = selectedSubagent.value;
      target = selectedSubagent;
    } else if (transferTarget) {
      toEntityId = transferTarget.value;
      target = transferTarget;
    }
    if (
      !transferAmount ||
      isNaN(transferAmount) ||
      Number(transferAmount) <= 0
    ) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!toEntityId) {
      toast.error("Select a recipient");
      return;
    }
    setTransferLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/wallet/transfer`,
        {
          fromEntityId: user.entityId,
          toEntityId,
          amount: Number(transferAmount),
          note: transferNote,
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      const receiptId = generateReceiptId();
      const invoiceNumber = generateInvoiceNumber();
      const dateFormatted = new Date().toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });

      setLastTransactionDetails({
        receiptId,
        invoiceNumber,
        date: dateFormatted,
        sender: {
          name: user.name,
          entityId: user.entityId,
          email: user.email || "",
          phone: user.phone || "",
          role: user.role || ""
        },
        receiver: {
          name: target ? (target.name || target.label) : toEntityId,
          entityId: toEntityId,
          email: target ? (target.email || "") : "",
          phone: target ? (target.phone || "") : ""
        },
        amount: Number(transferAmount),
        note: transferNote || "Wallet Balance Credit"
      });

      setShowTransferModal(false);
      setTransferAmount("");
      setTransferNote("");
      setShowSuccessModal(true);

      // Refresh correct wallet
      await fetchWalletData(toEntityId);
      toast.success("Transfer successful");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Transfer failed");
    } finally {
      setTransferLoading(false);
    }
  };

  const getWalletOwner = () => {
    if (activeWallet === "agents" && selectedAgent) {
      return {
        name: selectedAgent.name || selectedAgent.label,
        entityId: selectedAgent.entityId || selectedAgent.value,
        email: selectedAgent.email || "",
        phone: selectedAgent.phone || "",
        role: selectedAgent.role || "agent"
      };
    } else if (activeWallet === "subagents" && selectedSubagent) {
      return {
        name: selectedSubagent.name || selectedSubagent.label,
        entityId: selectedSubagent.entityId || selectedSubagent.value,
        email: selectedSubagent.email || "",
        phone: selectedSubagent.phone || "",
        role: selectedSubagent.role || "sub-agent"
      };
    } else {
      return {
        name: user?.name || "",
        entityId: user?.entityId || "",
        email: user?.email || "",
        phone: user?.phone || "",
        role: user?.role || ""
      };
    }
  };

  const extractEntityId = (note) => {
    if (!note) return "";
    const match = note.match(/(VH[A-Z0-9]+)/i);
    return match ? match[1].toUpperCase() : "";
  };

  const getUserDetailsById = (entityId) => {
    if (!entityId) return null;
    const cleanId = entityId.toUpperCase();
    
    if (user && user.entityId?.toUpperCase() === cleanId) {
      return user;
    }

    if (cleanId === "VHEAD00001" || cleanId === "ADMIN") {
      return {
        name: "Admin",
        entityId: "VHEAD00001",
        email: "support@drivesafesolutions.in",
        phone: "9178911367",
        role: "admin"
      };
    }
    
    let match = transferOptions.find(o => o.value?.toUpperCase() === cleanId);
    if (match) return match;

    match = agents.find(a => a.entityId?.toUpperCase() === cleanId);
    if (match) return match;

    match = subagents.find(s => s.entityId?.toUpperCase() === cleanId);
    if (match) return match;

    return null;
  };

  const handleDownloadReceiptForPastTransaction = async (tx) => {
    const dateFormatted = new Date(tx.createdAt || tx.time).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

    const owner = getWalletOwner();
    const isCredit = tx.type === "credit";

    let sender = {};
    let receiver = {};

    if (isCredit) {
      // Receiver is the wallet owner
      receiver = {
        name: owner.name,
        entityId: owner.entityId,
        email: owner.email,
        phone: owner.phone,
        role: owner.role
      };

      // Sender is the source
      const senderId = tx.fromEntity || extractEntityId(tx.note) || tx.fromWallet || "admin";
      const matchedUser = getUserDetailsById(senderId);
      sender = {
        name: matchedUser ? matchedUser.name : (senderId === "admin" ? "Admin" : senderId),
        entityId: senderId,
        email: matchedUser ? matchedUser.email : "",
        phone: matchedUser ? matchedUser.phone : "",
        role: matchedUser ? (matchedUser.role || "") : (senderId === "admin" ? "ADMIN" : "")
      };
    } else {
      // Sender is the wallet owner
      sender = {
        name: owner.name,
        entityId: owner.entityId,
        email: owner.email,
        phone: owner.phone,
        role: owner.role
      };

      // Receiver is the target
      const receiverId = tx.toEntity || extractEntityId(tx.note) || tx.toWallet || "receiver";
      const matchedUser = getUserDetailsById(receiverId);
      receiver = {
        name: matchedUser ? matchedUser.name : (receiverId === "receiver" ? "Agent/Sub-Agent" : receiverId),
        entityId: receiverId,
        email: matchedUser ? matchedUser.email : "",
        phone: matchedUser ? matchedUser.phone : ""
      };
    }

    const txInfo = {
      receiptId: tx.referenceId || generateReceiptId(),
      invoiceNumber: generateInvoiceNumber(),
      date: dateFormatted,
      sender,
      receiver,
      amount: Number(tx.amount),
      note: tx.note || tx.description || "Wallet Balance Credit"
    };

    const logoUrl = logo ? logo : logodefault;
    await generateReceiptPDF(txInfo, logoUrl);
  };



  // Admin: Add to agent/subagent wallet (same as transfer)
  const handleAdminCredit = handleTransfer;

  const renderAgentSelection = () => {
    if (userRole === "admin" && activeWallet === "agents") {
      return (
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Agent
          </label>
          <Select
            options={agents}
            value={selectedAgent}
            onChange={handleAgentChange}
            isLoading={loadingAgents}
            placeholder="Search by name, phone, or ID..."
            className="basic-single"
            classNamePrefix="select"
            filterOption={filterAgents}
            isSearchable={true}
          />
        </div>
      );
    }
    return null;
  };

  const renderSubagentSelection = () => {
    if (
      (userRole === "admin" || userRole === "agent") &&
      activeWallet === "subagents"
    ) {
      return (
        <div className="mb-4">
          {userRole === "admin" && (
            <div className="mb-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Agent
              </label>
              <Select
                options={agents}
                value={selectedAgent}
                onChange={handleAgentChange}
                isLoading={loadingAgents}
                placeholder="Select an agent first..."
                className="basic-single"
                classNamePrefix="select"
                filterOption={filterAgents}
                isSearchable={true}
              />
            </div>
          )}
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Select Subagent
          </label>
          <Select
            options={subagents}
            value={selectedSubagent}
            onChange={handleSubagentChange}
            isLoading={loadingSubagents}
            isDisabled={userRole === "admin" && !selectedAgent}
            placeholder={
              userRole === "admin" && !selectedAgent
                ? "Select an agent first"
                : "Select a subagent..."
            }
            className="basic-single"
            classNamePrefix="select"
          />
        </div>
      );
    }
    return null;
  };

  // Add transfer button for admin/agent
  const renderTransferButton = () => {
    if (
      (userRole === "admin" && activeWallet !== "my") ||
      (userRole === "agent" && activeWallet === "subagents")
    ) {
      return (
        <button
          className="ml-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          onClick={() => setShowTransferModal(true)}
        >
          Transfer Balance
        </button>
      );
    }
    return null;
  };

  // Render admin credit/transfer/deduct buttons based on context
  const renderAdminCreditButton = () => {
    if (userRole !== "admin" && userRole !== "agent") return null;

    // Add to My Wallet (admin only)
    if (activeWallet === "my" && userRole === "admin") {
      return (
        <button
          className="ml-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
          onClick={() => setShowAddMoneyModal(true)}
        >
          Add to My Wallet
        </button>
      );
    }

    // Add/Deduct Agent Wallet (admin only)
    if (userRole === "admin" && activeWallet === "agents" && selectedAgent) {
      return (
        <>
          <button
            className="ml-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            onClick={() => {
              setTransferTarget(selectedAgent);
              setShowTransferModal(true);
            }}
          >
            Add to Agent Wallet
          </button>
          <button
            className="ml-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            onClick={() => {
              setDeductTarget(selectedAgent);
              setShowDeductModal(true);
            }}
          >
            Deduct from Agent Wallet
          </button>
        </>
      );
    }

    // Add/Deduct Subagent Wallet (admin or agent)
    if (activeWallet === "subagents" && selectedSubagent) {
      return (
        <>
          {userRole === "admin" && selectedAgent && (
            <button
              className="ml-2 px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
              onClick={() => {
                setTransferTarget(selectedSubagent);
                setShowTransferModal(true);
              }}
            >
              Add to Subagent Wallet
            </button>
          )}
          <button
            className="ml-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            onClick={() => {
              setDeductTarget(selectedSubagent);
              setShowDeductModal(true);
            }}
          >
            Deduct from Subagent Wallet
          </button>
        </>
      );
    }
    return null;
  };

  // Admin: Deduct from agent/subagent wallet to admin wallet
  const handleDeduct = async () => {
    let fromEntityId = null;
    if (activeWallet === "agents" && selectedAgent) {
      fromEntityId = selectedAgent.value;
    } else if (activeWallet === "subagents" && selectedSubagent) {
      fromEntityId = selectedSubagent.value;
    } else if (deductTarget) {
      fromEntityId = deductTarget.value;
    }

    if (!deductAmount || isNaN(deductAmount) || Number(deductAmount) <= 0) {
      toast.error("Enter a valid amount");
      return;
    }

    if (!fromEntityId) {
      toast.error("Select a wallet to deduct from");
      return;
    }

    if (!deductedFor) {
      toast.error("Please specify what this deduction is for");
      return;
    }

    setDeductLoading(true);
    try {
      // const endpoint =
      //   userRole === "admin" ? "/wallet/admin-deduct" : "/wallet/agent-deduct";

      await axios.post(
        `${process.env.REACT_APP_API_URL}/wallet/admin-deduct`,
        {
          fromEntityId,
          toEntityId: user.entityId,
          amount: Number(deductAmount),
          note: deductedFor,
          // For agent deductions, include parentId for verification
          ...(userRole === "agent" && { parentId: user._id }),
        },
        {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        },
      );

      setShowDeductModal(false);
      setDeductAmount("");
      setDeductedFor("");
      // Refresh correct wallet
      await fetchWalletData(fromEntityId);
      toast.success("Deduction successful");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Deduction failed");
    } finally {
      setDeductLoading(false);
    }
  };

  // Custom filter for agent search only

  const filterAgents = (option, inputValue) => {
    if (!inputValue) return true;

    const searchTerm = inputValue.toLowerCase();
    const name = option.data.name?.toLowerCase() || "";
    const phone = option.data.phone?.toLowerCase() || "";
    const entityId = option.data.entityId?.toLowerCase() || "";

    return (
      name.includes(searchTerm) ||
      phone.includes(searchTerm) ||
      entityId.includes(searchTerm)
    );
  };
  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">
      <WalletHeader
        activeWallet={activeWallet}
        setActiveWallet={handleWalletTabChange}
        userRole={userRole}
        setShowAddMoneyModal={setShowAddMoneyModal}
        selectedAgent={selectedAgent}
        selectedSubagent={selectedSubagent}
      />

      <main className="container mx-auto px-4 py-6">
        {renderAgentSelection()}
        {renderSubagentSelection()}

        {/* Transfer Button & Admin Credit Button */}
        <div className="flex items-center mb-4">
          {renderTransferButton()}
          {renderAdminCreditButton()}
        </div>

        {!loading && activeWallet === "agents" && !selectedAgent && (
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <p className="text-gray-500">
              Please select an agent to view their wallet
            </p>
          </div>
        )}

        {!loading && activeWallet === "subagents" && !selectedSubagent && (
          <div className="bg-white p-4 rounded-lg shadow mb-4">
            <p className="text-gray-500">
              {userRole === "admin" && !selectedAgent
                ? "Please select an agent first"
                : "Please select a subagent to view their wallet"}
            </p>
          </div>
        )}

        {(activeWallet === "my" ||
          (activeWallet === "agents" && selectedAgent) ||
          (activeWallet === "subagents" && selectedSubagent)) && (
          <>
            {activeWallet === "my" ? (
              <BalanceCard
                balance={balance}
                currency={currency}
                renderWalletTitle={renderWalletTitle}
                loading={loading}
              />
            ) : (
              <AgentBalanceCard
                entityId={
                  activeWallet === "agents"
                    ? selectedAgent?.value
                    : selectedSubagent?.value
                }
                title={renderWalletTitle()}
              />
            )}

            <div className="flex border-b border-gray-200 mb-6">
              {["transactions"].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-medium text-sm ${
                    activeTab === tab
                      ? "text-indigo-600 border-b-2 border-indigo-600"
                      : "text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p>Loading transactions...</p>
              </div>
            ) : activeTab === "transactions" ? (
              <TransactionList
                transactions={currentTransactions}
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onDownloadReceipt={handleDownloadReceiptForPastTransaction}
              />
            ) : (
              <div className="bg-white p-5 rounded-xl shadow-sm">
                <div className="h-64 flex items-center justify-center">
                  <p className="text-gray-400">Analytics coming soon</p>
                </div>
              </div>
            )}
          </>
        )}
      </main>

      <AddMoneyModal
        showAddMoneyModal={showAddMoneyModal}
        setShowAddMoneyModal={setShowAddMoneyModal}
        addMoneyAmount={addMoneyAmount}
        setAddMoneyAmount={setAddMoneyAmount}
        renderWalletTitle={renderWalletTitle}
        handleAddMoney={handleAddMoney}
        disabled={activeWallet !== "my"} // Only enable for own wallet
      />

      {/* Success Modal */}
      {showSuccessModal && lastTransactionDetails && (
        <Modal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6 text-center">
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Transfer Successful</h3>
              <p className="text-gray-500 mb-6">
                INR {lastTransactionDetails.amount.toFixed(2)} has been credited successfully.
              </p>

              <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left text-sm space-y-2 border border-gray-100">
                <div className="flex justify-between">
                  <span className="text-gray-500">Receipt ID:</span>
                  <span className="font-semibold text-gray-800">{lastTransactionDetails.receiptId}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Receiver:</span>
                  <span className="font-semibold text-gray-800">{lastTransactionDetails.receiver.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Date:</span>
                  <span className="font-semibold text-gray-800">{lastTransactionDetails.date}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="flex-1 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-semibold rounded-lg transition"
                >
                  Close
                </button>
                <button
                  onClick={async () => {
                    const logoUrl = logo ? logo : logodefault;
                    await generateReceiptPDF(lastTransactionDetails, logoUrl);
                  }}
                  className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition flex items-center justify-center gap-2"
                >
                  Download Receipt
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {showDeductModal && (
        <Modal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4">
                {userRole === "admin"
                  ? "Deduct to Admin Wallet"
                  : "Deduct to Your Wallet"}
              </h3>

              {/* Show who we're deducting from */}
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="font-medium">Deducting from:</p>
                <p>{selectedAgent?.label || selectedSubagent?.label}</p>
              </div>

              {/* Rest of the modal remains the same */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount to Deduct (₹)
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full border p-2 rounded"
                  value={deductAmount}
                  onChange={(e) => setDeductAmount(e.target.value)}
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Deducted For (required)
                </label>
                <input
                  type="text"
                  className="w-full border p-2 rounded"
                  value={deductedFor}
                  onChange={(e) => setDeductedFor(e.target.value)}
                  placeholder="What is this deduction for?"
                  required
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeductModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  disabled={deductLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeduct}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                  disabled={deductLoading}
                >
                  {deductLoading ? "Processing..." : "Deduct"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* Admin Credit Modal */}
      {showTransferModal && (
        <Modal>
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
              <h3 className="text-lg font-semibold mb-4">Transfer Balance</h3>

              {/* Show sender info */}
              <div className="mb-4 p-3 bg-gray-50 rounded">
                <p className="font-medium">Transferring from:</p>
                <p>
                  {user.name} ({user.entityId})
                </p>
              </div>

              {/* Only show recipient selection if not in subagent tab with selected subagent */}
              {!(activeWallet === "subagents" && selectedSubagent) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient
                  </label>
                  <Select
                    options={transferOptions}
                    value={transferTarget}
                    onChange={setTransferTarget}
                    placeholder="Select recipient..."
                    filterOption={filterAgents}
                    isSearchable={true}
                  />
                </div>
              )}

              {/* Show current recipient if in subagent tab */}
              {activeWallet === "subagents" && selectedSubagent && (
                <div className="mb-4 p-3 bg-gray-50 rounded">
                  <p className="font-medium">Transferring to:</p>
                  <p>{selectedSubagent.label}</p>
                </div>
              )}

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount
                </label>
                <input
                  type="number"
                  min="1"
                  className="w-full border p-2 rounded"
                  value={transferAmount}
                  onChange={(e) => setTransferAmount(e.target.value)}
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Note (optional)
                </label>
                <input
                  type="text"
                  className="w-full border p-2 rounded"
                  value={transferNote}
                  onChange={(e) => setTransferNote(e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowTransferModal(false)}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  disabled={transferLoading}
                >
                  Cancel
                </button>
                <button
                  onClick={handleTransfer}
                  className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
                  disabled={transferLoading}
                >
                  {transferLoading ? "Transferring..." : "Transfer"}
                </button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Wallet;
